/* eslint-disable @typescript-eslint/no-explicit-any */
import cx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  ActionIcon,
  Anchor,
  Center,
  Group,
  Pill,
  ScrollArea,
  Table,
  Text,
  Tooltip,
  UnstyledButton,
  rem,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconCopy,
  IconPencil,
  IconSelector,
  IconTrash,
} from "@tabler/icons-react";
import {
  GetCurrentUserOutput,
  fetchAuthSession,
  getCurrentUser,
} from "aws-amplify/auth";
import { put } from "aws-amplify/api";
import { ThProps } from "../../InterfaceProps";
import ConfirmDeleteTemplateModal from "../ConfirmDeleteTemplateModal";
import EditTemplateModal from "../EditTemplateModal";
import classes from "./TemplateTable.module.css";

interface RowData {
  name: string;
  location: string;
  updatedAt: string;
  createdAt: string;
}

const TemplateTable = ({ data, fetchTemplates }: any) => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [template, setTemplate] = useState<any>(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [modalConfirmDeleteOpened, setModalConfirmDeleteOpened] =
    useState(false);
  const viewport = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<any>(null);
  const [scrollPosition, onScrollPositionChange] = useState({ x: 0, y: 0 });
  useEffect(() => {
    setScrolled(scrollPosition.y != 0);
  }, [scrollPosition]);

  const [sortedData, setSortedData] = useState<any>([]);
  useEffect(() => {
    setSortedData(data);
  }, [data]);

  const filterData = (data: RowData[]) => {
    return data;
  };

  const handleDuplicateTemplate = async (template: { id: any }) => {
    const user: GetCurrentUserOutput = await getCurrentUser();
    const { identityId } = await fetchAuthSession();
    const init: any = {
      response: true,
      headers: {},
      body: {
        template: template,
        username: user.username,
        userId: identityId,
      },
    };
    try {
      await put({
        apiName: "DuplicateTemplate",
        path: `/duplicatetemplate/${template.id}`,
        options: init,
      });
    } catch (err) {
      console.log("duplicateTemplate error: ", err);
    }
    fetchTemplates();
  };

  const [columns, setColumns] = useState<any>([
    { id: 1, name: "Name", sortBy: "name" },
    { id: 2, name: "Type", isResize: true },
    { id: 3, name: "Created", sortBy: "createdAt" },
    { id: 4, name: "Updated", sortBy: "updatedAt" },
    { id: 5, name: "Actions" },
  ]);

  const [resizing, setResizing] = useState({
    isResizing: false,
    columnIndex: -1,
    startX: 0,
    startWidth: 0,
  });

  const handleMouseDown = (e: any, index: number) => {
    const colEl = document.getElementById(`col-${columns[index].id}`);
    setResizing({
      isResizing: true,
      columnIndex: index,
      startX: e.clientX,
      startWidth: colEl?.clientWidth as number,
    });
  };

  const handleMouseMove = (e: any) => {
    if (!resizing.isResizing) return;
    let newWidth = resizing.startWidth + (e.clientX - resizing.startX);
    if (newWidth < 50) {
      newWidth = 50;
    }
    setColumns((prevColumns: any) =>
      prevColumns.map((col: any, index: number) =>
        index === resizing.columnIndex ? { ...col, width: newWidth } : col,
      ),
    );
  };

  const handleMouseUp = () => {
    try {
      if (resizing.isResizing) {
        setResizing({ ...resizing, isResizing: false });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const rows = sortedData.map((row: any) => {
    const strCreated = moment(row.createdAt).format("MM/DD/YYYY, hh:mm A");
    const strUpdated = moment(row.updatedAt).format("MM/DD/YYYY, hh:mm A");
    return (
      <Table.Tr key={row.id} style={{ cursor: "auto" }}>
        <Table.Td>
          <Anchor
            onClick={() =>
              navigate(`/template-detail/${row.id}`, {
                state: row,
              })
            }
          >
            <Group gap={5}>{row.name}</Group>
          </Anchor>
        </Table.Td>
        <Table.Td>
          <Pill
            bg={row.advanced ? "#003554" : "#B8D4F0"}
            size="lg"
            h={30}
            pr={15}
            pl={15}
          >
            <Text c={row.advanced ? "white" : "black"} fw={500} fz={13} mt={5}>
              {row.advanced ? "Advanced" : "Standard"}
            </Text>
          </Pill>
        </Table.Td>
        <Table.Td>{strCreated}</Table.Td>
        <Table.Td>{strUpdated}</Table.Td>
        <Table.Td className="text-center" w={120}>
          <Tooltip label="Edit template" withArrow>
            <ActionIcon
              size="sm"
              mx={3}
              variant="subtle"
              color="gray"
              radius={20}
              onClick={() => {
                setTemplate(row);
                setModalOpened(true);
              }}
            >
              <IconPencil size={20} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Duplicate template" withArrow>
            <ActionIcon
              size="sm"
              mx={3}
              variant="subtle"
              color="gray"
              onClick={() => {
                setTemplate(row);
                handleDuplicateTemplate(row);
              }}
            >
              <IconCopy size={20} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete template" withArrow>
            <ActionIcon
              mx={3}
              color="red"
              variant="subtle"
              size="sm"
              radius={20}
              onClick={() => {
                setTemplate(row);
                setModalConfirmDeleteOpened(true);
              }}
            >
              <IconTrash size={20} />
            </ActionIcon>
          </Tooltip>
        </Table.Td>
      </Table.Tr>
    );
  });

  const Th = ({
    children,
    reversed,
    sorted,
    onSort,
    index,
    style,
    id,
  }: ThProps) => {
    const Icon = sorted
      ? reversed
        ? IconChevronUp
        : IconChevronDown
      : IconSelector;
    return (
      <Table.Th id={id} className={classes.th} style={style}>
        <UnstyledButton onClick={onSort} className={classes.control}>
          <Group justify="space-between">
            <Text fw={500} fz="sm">
              {children}
            </Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
        <div
          className="resize-handle"
          onMouseDown={(e) => handleMouseDown(e, index)}
        ></div>
      </Table.Th>
    );
  };

  const setSorting = (field: keyof RowData) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(data, { sortBy: field, reversed }));
  };

  function sortData(
    data: RowData[],
    payload: { sortBy: keyof RowData | null; reversed: boolean },
  ) {
    const { sortBy } = payload;
    if (!sortBy) {
      return filterData(data);
    }
    return filterData(
      [...data].sort((a: any, b: any) => {
        if (payload.reversed) {
          return b[sortBy].localeCompare(a[sortBy]);
        }

        return a[sortBy].localeCompare(b[sortBy]);
      }),
    );
  }

  const [sortBy, setSortBy] = useState<keyof RowData | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  return (
    <>
      <ScrollArea
        h={700}
        w={"100%"}
        scrollbars="xy"
        ref={scrollAreaRef}
        onScrollPositionChange={onScrollPositionChange}
        viewportRef={viewport}
      >
        <div onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
          <Table className="vetscribe-table">
            <Table.Thead
              className={cx(classes.header, { [classes.scrolled]: scrolled })}
            >
              <Table.Tr>
                {columns.map((column: any, index: number) => {
                  if (column.sortBy) {
                    return (
                      <Th
                        key={index}
                        sorted={sortBy === column.sortBy}
                        reversed={reverseSortDirection}
                        onSort={() => setSorting(column.sortBy as any)}
                        index={index}
                        id={`col-${column.id}`}
                        style={{ width: column?.width }}
                      >
                        {column.name}
                      </Th>
                    );
                  }
                  return (
                    <Table.Th
                      key={index}
                      className="text-center th-action"
                      fw={500}
                      fz={"sm"}
                      style={{ width: column?.width }}
                    >
                      <UnstyledButton className="control">
                        <Group
                          justify={column.name == "Actions" ? "center" : ""}
                        >
                          {column.name}
                        </Group>
                      </UnstyledButton>
                      {column.isResize ? (
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, index)}
                        ></div>
                      ) : null}
                    </Table.Th>
                  );
                })}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </div>
      </ScrollArea>
      <EditTemplateModal
        modalOpened={modalOpened}
        setModalOpened={setModalOpened}
        template={template}
        fetchTemplates={fetchTemplates}
        setTemplate={setTemplate}
        setModalConfirmDeleteOpened={setModalConfirmDeleteOpened}
      />
      <ConfirmDeleteTemplateModal
        modalOpened={modalConfirmDeleteOpened}
        setModalOpened={setModalConfirmDeleteOpened}
        template={template}
        fetchTemplates={fetchTemplates}
      />
    </>
  );
};

export default TemplateTable;
